import { type States, states } from "@repo/lib/constants/states";
import type { Tenant } from "@repo/types";
import { ComboboxInput } from "@repo/ui/components/Base/ComboboxInput";
import { Button } from "@repo/ui/components/Base/Button";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { capitalize } from "@repo/shared/utils";
import { useGetOrganizationTypes } from "@/hooks/queryHooks/configHooks";
import {
  useGetMunicipalities,
  useGetStates,
} from "@/hooks/queryHooks/stripeHooks";

export const SelectMunicipalityStep = ({
  isSubmitting,
}: {
  isSubmitting: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<Partial<Tenant>>();
  const { data: organizationTypes } = useGetOrganizationTypes();

  const { data: availableStates } = useGetStates();
  const { data: availableMunicipalities, loaded: municipalitiesLoaded } =
    useGetMunicipalities(values.organizationState ?? "");

  const availableTypes = useMemo(() => {
    if (!availableMunicipalities) return [];
    if ("error" in availableMunicipalities) return [];
    const types = new Map();
    availableMunicipalities.map((m) =>
      types.set(m.type, { id: m.typeId, type: m.type })
    );
    return Array.from(types.values()).map((t) => t);
  }, [availableMunicipalities]);

  return (
    <>
      <h1 className="bg-tertiary rounded-lg text-xl text-white p-2">Step 1</h1>
      <h2 className="text-xl">Select your municipality to get started</h2>

      {values.friendlyId && organizationTypes?.organizationTypes && (
        <>
          <h3 className="text-xl">1.1. Select Your State</h3>
          <SelectInput
            label="State"
            onValueChange={(value) => setFieldValue("organizationState", value)}
            options={
              availableStates?.map((state) => (
                <SelectItem key={state} value={state}>
                  {states[state as keyof States]}
                </SelectItem>
              )) ?? []
            }
            value={values.organizationState}
          />
        </>
      )}
      {values.friendlyId &&
        values.organizationState &&
        values.organizationState !== "" &&
        availableTypes && (
          <>
            {!municipalitiesLoaded ? (
              <Spinner />
            ) : (
              <>
                <h3 className="text-xl">1.2. Select Your Municipality Type</h3>
                <ComboboxInput
                  placeholder="Select Type"
                  emptyText="No Types Found"
                  label="Municipality Type"
                  id="municipality_type"
                  onChange={(value, id) => {
                    setFieldValue("organizationMunicipalityTypeId", `${id}`);
                    setFieldValue(
                      "organizationMunicipalityType",
                      value.split("-")[1]
                    );
                    setFieldValue("organizationMunicipalityName", undefined);
                  }}
                  options={availableTypes.map((type) => ({
                    value: `${type.id}-${type.type}`,
                    label: type.type,
                  }))}
                  value={values.organizationMunicipalityTypeId}
                />
              </>
            )}
            {values.organizationMunicipalityType &&
              values.organizationMunicipalityType !== "" &&
              availableMunicipalities && (
                <>
                  {!municipalitiesLoaded ? (
                    <Spinner />
                  ) : (
                    <>
                      <h3 className="text-xl">1.3. Select Your Municipality</h3>
                      <ComboboxInput
                        placeholder="Select Municipality"
                        emptyText="No Municipalities Found"
                        containerStyle="w-full"
                        id="municipality"
                        onChange={(value, id) => {
                          setFieldValue("organizationMunicipalityId", id);
                          setFieldValue(
                            "organizationMunicipalityName",
                            capitalize(value.split("-")[1])
                          );
                          setFieldValue(
                            "organizationMunicipalityPopulation",
                            availableMunicipalities.find(
                              (m) => `${m.id}` === value.split("-")[0]
                            )?.population ?? 0
                          );
                        }}
                        options={availableMunicipalities
                          .filter(
                            (m) =>
                              `${m.typeId}` ===
                              values.organizationMunicipalityTypeId?.split(
                                "-"
                              )[0]
                          )
                          .map((municipality) => ({
                            value: `${municipality.id}-${municipality.name}`,
                            label: `${municipality.name}${municipality.type !== "County" ? `, ${municipality.county} County` : ""}`,
                          }))}
                        value={values.organizationMunicipalityId}
                      />
                    </>
                  )}
                </>
              )}
          </>
        )}
      {values.friendlyId &&
        values.organizationMunicipalityType &&
        values.organizationMunicipalityName && (
          <Button type="submit" disabled={isSubmitting} className="mt-4">
            Continue
          </Button>
        )}
    </>
  );
};
